/**
 * @file Header.jsx
 * @description Responsive and accessible site header for the FinTorro homepage.
 * Includes sticky navigation, logo, dark/light theme toggle, and mobile-friendly hamburger menu.
 * Prevents scroll when menu is open, and ensures smooth navigation experience on mobile devices.
 */

import React, { useState, useEffect } from "react";
import "../styles/homepage.css";
import logo from "../assets/fintorro-logo.png";

/**
 * `Header` Component – renders the sticky top navigation bar.
 *
 * @returns {JSX.Element} Responsive site header with mobile menu and navigation links.
 *
 * @example
 * ```jsx
 * import Header from './components/Header';
 * 
 * function App() {
 *   return (
 *     <>
 *       <Header />
 *       <MainContent />
 *     </>
 *   );
 * }
 * ```
 */
const Header = () => {
  const [theme, setTheme] = useState("light");
  const [isOpen, setIsOpen] = useState(false);

  // Load and apply saved theme (light/dark)
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    document.documentElement.setAttribute("data-theme", savedTheme);
    setTheme(savedTheme);
  }, []);

  // Add/remove class to body when menu is toggled
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
  }, [isOpen]);

  /**
   * Toggle between dark and light themes.
   * Updates HTML `data-theme` attribute and localStorage.
   */
  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", nextTheme);
    localStorage.setItem("theme", nextTheme);
    setTheme(nextTheme);
  };

  /**
   * Handles link click inside nav menu.
   * Closes the menu and delays navigation slightly for smooth UX.
   *
   * @param {React.MouseEvent} e - The anchor click event
   */
  const handleLinkClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href");
    setIsOpen(false);
    setTimeout(() => {
      window.location.href = href;
    }, 150);
  };

  return (
    <header className="sticky-header">
      <div className="container header-container">
        {/* Logo */}
        <div className="logo-wrapper">
          <img src={logo} alt="Fintorro Logo" className="logo-image" />
        </div>

        {/* Mobile Hamburger Menu Toggle */}
        <div
          className="hamburger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
          aria-controls="main-navigation"
        >
          <div className={`bar ${isOpen ? "rotate-top" : ""}`} />
          <div className={`bar ${isOpen ? "hide-middle" : ""}`} />
          <div className={`bar ${isOpen ? "rotate-bottom" : ""}`} />
        </div>

        {/* Navigation Menu */}
        <nav
          id="main-navigation"
          className={`nav-links ${isOpen ? "open" : ""}`}
          role="navigation"
        >
          <ul>
            <li><a href="/" onClick={handleLinkClick}>Learn with AI</a></li>
            <li><a href="/agents" onClick={handleLinkClick}>AI Agents</a></li>
            <li>
              <a
                href="https://blog.fintorro.com/trading/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                Magazine
              </a>
            </li>
            <li>
              <a
                href="https://www.blog.fintorro.com/trading/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                Subscribe
              </a>
            </li>
            <li className="mobile-only">
              <a href="/login" className="login-link" onClick={handleLinkClick}>
                Login
              </a>
            </li>
          </ul>
        </nav>

        {/* Desktop-only CTA buttons */}
        <div className="header-buttons desktop-only">
          <a href="/login" className="login-button">Login</a>
          <a href="/login" className="cta-button hide-on-mobile">Get Started Free</a>
        </div>
      </div>
    </header>
  );
};

export default Header;






// import React, { useState, useEffect } from "react";
// import "../styles/homepage.css";
// import logo from "../assets/fintorro-logo.png"; // Adjust if needed

// const Header = () => {
//   const [theme, setTheme] = useState("light");
//   const [isOpen, setIsOpen] = useState(false);

//   useEffect(() => {
//     const savedTheme = localStorage.getItem("theme") || "light";
//     document.documentElement.setAttribute("data-theme", savedTheme);
//     setTheme(savedTheme);
//   }, []);

//   useEffect(() => {
//     if (isOpen) {
//       document.body.classList.add('menu-open');
//     } else {
//       document.body.classList.remove('menu-open');
//     }
//   }, [isOpen]);

  

//   const toggleTheme = () => {
//     const nextTheme = theme === "dark" ? "light" : "dark";
//     document.documentElement.setAttribute("data-theme", nextTheme);
//     localStorage.setItem("theme", nextTheme);
//     setTheme(nextTheme);
//   };

//   return (
//     <header className="sticky-header">
//       <div className="container header-container">
//         <div className="logo-wrapper">
//           <img src={logo} alt="Fintorro Logo" className="logo-image" />
//         </div>

//         <div
//           className="hamburger"
//           onClick={() => setIsOpen(prev => !prev)}
//           aria-label="Toggle navigation"
//            aria-expanded={isOpen}
//   aria-controls="main-navigation"
//         >
//           <div className={`bar ${isOpen ? "rotate-top" : ""}`} />
//           <div className={`bar ${isOpen ? "hide-middle" : ""}`} />
//           <div className={`bar ${isOpen ? "rotate-bottom" : ""}`} />
//         </div>

//         <nav className={`nav-links ${isOpen ? "open" : ""}`} role="navigation">
//           <ul>
//             <li><a href="/" onClick={() => setIsOpen(false)}>Learn with AI</a></li>
//             <li><a href="/agents" onClick={() => setIsOpen(false)}>AI Agents</a></li>
//             <li>
//               <a
//                 href="https://blog.fintorro.com/trading/"
//                 rel="noopener noreferrer"
//                 onClick={() => setIsOpen(false)}
//               >
//                 Magazine
//               </a>
//             </li>
//             <li><a href="https://www.blog.fintorro.com/subscribe/" onClick={() => setIsOpen(false)}>Subscribe</a></li>
//             <li className="mobile-only">
//               <a href="/login" className="login-link" onClick={() => setIsOpen(false)}>Login</a>
//             </li>
//           </ul>
//         </nav>

//         <div className="header-buttons desktop-only">
//           <a href="/login" className="login-button">Login</a>
//           <a href="/login" className="cta-button hide-on-mobile">Get Started Free</a>
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;
